import { usePlan } from "@/entities/Plan";
import { PlanTypeInterface } from "@/entities/Plan/model/plan.types";
import { useAuth } from "@/features/Auth/lib/useAuth";
import { useNotificationActions } from "@/features/Notification";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { filterPlanTypeByUser, parseUsersInDepartment } from "@/shared/lib/users";
import { UserInterface } from "@/shared/model/api.types";
import { useEffect, useMemo, useState } from "react";
import { CreatePlanPopupForm } from "./CreatePlanPopup.model";
import { usePlanActions } from "@/entities/Plan/lib/usePlanActions";
import { useHeaderDashboard } from "../../../lib/useHeaderDashboard";
import { isSalesSystemAccount, salesAdministrationDepartments } from '@/features/Auth/lib/salesAccess';


const useCreatePlanPopup = (onClose: () => void, id?: number) => {
  const { users, departments, subGenDepartments, loading: workspaceLoading, error: workspaceError } = useWorkspace();
  const { planTypes, loading: planTypesLoading, error: planTypesError, myPlans } = usePlan();
  const { createPlan } = usePlanActions();
  const { user, userRole } = useAuth();
  const availableDepartments = useMemo(
    () => isSalesSystemAccount(user) ? salesAdministrationDepartments(departments) : subGenDepartments,
    [departments, subGenDepartments, user]
  );
  const { setSelectedPlan } = useHeaderDashboard();
  const { topAlert } = useNotificationActions();  
  
  // Если передан id, ищем план по id
  const planById = id ? myPlans?.find(plan => plan.id === id) : null;

  const initForm = useMemo((): CreatePlanPopupForm => {
    if (planById) {
      const { plan_type_id, year, month, target_value, user_id, department_id } = planById;
      return { plan_type_id, year, month, target_value, user_id, department_id };
    }
    return {
      plan_type_id: 0,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      target_value: 0,
      user_id: 0,
      department_id: 0,
    }
  }, [planById]);

  const [form, setForm] = useState<CreatePlanPopupForm>(initForm);
  const [visibleMonth, setVisibleMonth] = useState(true);
  const [filteredPlanTypes, setFilteredPlanTypes] = useState<PlanTypeInterface[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserInterface[]>([]);
  const [description, setDescription] = useState('Выберите тип плана');
  
  useEffect(() => {
    if (availableDepartments.length > 0) {
      if (planById) {
        setForm(prev => {
          return {
            ...prev,
            department_id: planById.department_id
          };
        });
      }else{
        setForm(prev => {
          return {
            ...prev,
            department_id: availableDepartments[0].id
          };
        });
      }
    }
  }, [availableDepartments, planById]);

  // useEffect(() => {
  //   if (
  //     filteredUsers.length > 0 &&
  //     form.department_id &&
  //     form.user_id !== filteredUsers[0].id
  //   ) {
  //     setForm(prev => {
  //       if (prev.user_id === filteredUsers[0].id) return prev;
  //       return {
  //         ...prev,
  //         user_id: filteredUsers[0].id
  //       };
  //     });
  //   }
  // }, [filteredUsers, form.department_id, form.user_id]);

  // useEffect(() => {
  //   if (filteredPlanTypes.length > 0 && form.user_id) {
  //     setForm(prev => {
  //       if (prev.plan_type_id === filteredPlanTypes[0].id) return prev;
  //       return {
  //         ...prev,
  //         plan_type_id: filteredPlanTypes[0].id
  //       };
  //     });
  //   }
  // }, [filteredPlanTypes, form.user_id, form.plan_type_id]);

  // useEffect(() => {
  //   if (form.plan_type_id && visibleMonth) {
  //     const currentMonth = new Date().getMonth() + 1;
  //     setForm(prev => {
  //       if (prev.month === currentMonth) return prev;
  //       return {
  //         ...prev,
  //         month: currentMonth
  //       };
  //     });
  //   }
  // }, [form.plan_type_id, visibleMonth, form.month]);

  useEffect(() => {
    const planTypesResult = (filterPlanTypeByUser(
      planTypes,
      form && "user_id" in form ? form.user_id ?? null : null,
      availableDepartments,
      userRole,
      users
    ) ?? []).filter(pt => pt.code !== "MULTY_COMPANY_EMPLOYEE_MONTH");
    setFilteredPlanTypes(prev => {
      const isSame = Array.isArray(planTypesResult) && Array.isArray(prev) && planTypesResult.length === prev.length && planTypesResult.every((v, i) => v.id === prev[i].id);
      return isSame ? prev : (planTypesResult ?? []);
    });
    setDescription(planTypesResult.find(pt => pt.id === form.plan_type_id)?.description ?? 'Выберите тип плана')
  }, [planTypes, form.user_id, availableDepartments, userRole, users, form]);
  
  useEffect(() => {
    if (filteredPlanTypes && filteredPlanTypes.length > 0) {
      const hasMonthType = planTypes.find(pt => pt.id === form.plan_type_id)?.code.includes("MONTH") ?? false;
      setVisibleMonth(prev => prev === hasMonthType ? prev : hasMonthType);
    } else {
      setVisibleMonth(prev => prev === false ? prev : false);
    }
  }, [filteredPlanTypes, form.plan_type_id, planTypes]);

  useEffect(() => {
    const usersResult = parseUsersInDepartment(
      users,
      form && "department_id" in form ? (form.department_id as number) : null
    );
    setFilteredUsers(prev => {
      const isSame = Array.isArray(usersResult) && Array.isArray(prev) && usersResult.length === prev.length && usersResult.every((v, i) => v.id === prev[i].id);
      return isSame ? prev : (usersResult ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, form.department_id, form]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (!prev) return prev;
      const newForm = {
        ...prev,
        [name]:
          [
            "target_value",
            "year",
            "month",
            "plan_type_id",
            "user_id",
            "department_id",
          ].includes(name)
            ? Number(value)
            : value,
      };
      if (name === "user_id") {
        newForm.plan_type_id = 0;
      }
      const selectedPlanType = planTypes?.find((p) => p.id === newForm.plan_type_id);
      const isMonthPlan = selectedPlanType?.code.includes("MONTH");
      if (!isMonthPlan) {
        newForm.month = null;
      }
      return newForm;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (
      !form.target_value || form.target_value === 0 ||
      !form.year || form.year === 0 ||
      (visibleMonth && !form.month) || form.month === 0 ||
      !form.department_id || form.department_id === 0 ||
      !form.user_id || form.user_id === 0 ||
      !form.plan_type_id || form.plan_type_id === 0
    ) {
      topAlert("Пожалуйста, заполните все обязательные поля", "warning", 1000);
      return;
    }
    const res = await createPlan(form);
    if(res){
      setSelectedPlan(res.id)
      onClose();
    }
  }

  return {
    form,
    users: filteredUsers,
    departments: availableDepartments,
    planTypes: filteredPlanTypes,
    visibleMonth,
    loading: {
      users: workspaceLoading.users,
      departments: workspaceLoading.departments,
      planTypes: planTypesLoading.planTypes,
    },
    error: {
      users: workspaceError.users,
      departments: workspaceError.departments,
      planTypes: planTypesError.planTypes,
    },
    description,
    handleChange,
    handleSave
  }
}

export default useCreatePlanPopup;
